import { Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import NavigationDrawer from "../../../components/NavigationDrawer";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import { baseUrl } from "../../../services";
import {
  getUserWithdrawRequest,
  updateUserWithdrawRequest,
} from "../../../services/usersApiFn";
import { handleOpenSnackBar } from "../../../utils";
import RejectReasonDialog from "./ReasonDialog";

const headerData = [
  "createdAt",
  "userDetail",
  "bankDetail",
  "amount",
  "status",
  "reason",
  "receipt",
];

const tableRows = [
  "created_date",
  "withdraw_user_detail",
  "withdraw_bank_detail",
  "withdraw_amount",
  "withdraw_status",
  "withdraw_reason",
  "withdraw_upload_receipt",
];

function AdminWithdraw() {
  const imageRef = useRef();
  const dispatch = useDispatch();
  const openRejectReasonDialog = useRef();
  const { t } = useTranslation(["withdraw", "common"]);
  const [allRequests, setAllRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    fetchUserwithdrawRequest();
  }, []);

  const fetchUserwithdrawRequest = async () => {
    try {
      setIsLoading(true);
      const response = await getUserWithdrawRequest();
      if (response.data.status === "success") {
        setAllRequests(response.data.data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeWithdrawStatus = async (status, row, reason = "") => {
    if (status === "rejected" && !reason) {
      setSelectedRow(row);
      openRejectReasonDialog.current?.openDialog();
      return;
    }

    try {
      const payload = { status, type: "status-update" };
      if (reason) payload.reason = reason;

      setIsLoading(true);
      const response = await updateUserWithdrawRequest(row._id, payload);
      if (response.data.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        fetchUserwithdrawRequest();
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadReceipt = async (row, event) => {
    try {
      setIsLoading(true);

      const formData = new FormData();
      formData.append("image", event.target.files[0]);

      const response = await axios.post(`${baseUrl}image/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const imageUrl = response?.data?.url;

      if (imageUrl) {
        const updateResponse = await updateUserWithdrawRequest(row._id, {
          receipt: imageUrl,
          type: "receipt-upload",
        });

        if (updateResponse.data.status === "success") {
          handleOpenSnackBar("success", t("success.receiptSuccess", { ns: "common" }));
          fetchUserwithdrawRequest();
        } else {
          handleOpenSnackBar("error", updateResponse.data.message);
        }
      }
    } catch (error) {
      handleOpenSnackBar("error", t("error.imageUploadFailed", { ns: "common" }));
    } finally {
      setIsLoading(false);
      imageRef.current.value = null;
    }
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />

      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => handleUploadReceipt(selectedRow, e)}
      />

      <PaperContainer
        title={t("withdrawRequests")}
        subtitle={t("withdrawRequestsDesc")}
      >
        <DynamicPaginatedTable
          showPagination={true}
          minWidth={1400}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allRequests || []}
          handleChangeWithdrawStatus={handleChangeWithdrawStatus}
          handleUploadReceipt={(row, e) => {
            setSelectedRow(row);
            imageRef.current.click();
          }}
        />
        {allRequests?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}
          </Typography>
        )}
      </PaperContainer>

      <RejectReasonDialog
        ref={openRejectReasonDialog}
        onConfirm={(reason) =>
          handleChangeWithdrawStatus("rejected", selectedRow, reason)
        }
      />
    </NavigationDrawer>
  );
}

export default AdminWithdraw;
